import React, {useEffect, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import {ResponseType} from "../utils/awsAmplifyUtil";
import useNotification from "../utils/notificationUtil";
import {API_DATA_COUNT_LIMIT} from "../constants/constants";

export const STATUSES = {
  IDLE: 'idle',
  FETCHING: 'fetching',
  SUCCESS: 'success',
  FAILURE: 'failure',
} as const;

export type Status = typeof STATUSES[keyof typeof STATUSES];

export type QueryFnData<T = unknown> = Promise<ResponseType<T>>;

export default function useFetchAllQuery<
  TResultData,
  TError = any,
>({
    queryFn,
    needToFetchMore,
    limit = API_DATA_COUNT_LIMIT,
    onError,
    onSuccess,
    onFetch,
    enableAutoFetch = false,
  }: {
  queryFn: (params: { limit: number, skip: number }) => QueryFnData<TResultData>;
  needToFetchMore: (data: TResultData, limit: number) => boolean;
  limit?: number;
  onError?: (error: TError) => void;
  onSuccess?: (data: TResultData) => void;
  onFetch?: (data: TResultData) => void;
  enableAutoFetch?: boolean;
}) {
  /** @see https://ja.react.dev/learn/synchronizing-with-effects#what-are-good-alternatives-to-data-fetching-in-effects */
  let ignore = false;
  const [data, setData] = useState<TResultData[]>([]);
  const [error, setError] = useState<TError | null>(null);
  const [status, setStatus] = useState<Status>(STATUSES.IDLE);

  const resetError = () => setError(null);
  const resetData = () => setData([]);

  const handleError = (e: TError) => {
    setStatus(STATUSES.FAILURE);
    setError(e);
    onError?.(e);
  }

  const fetcher = async (skip: number) => {
    const result = await queryFn({limit: limit + skip, skip});
    if (result.isSuccess) {
      const additionalData = result.data;
      if (additionalData !== undefined) {
        setData((prev) => [...prev, additionalData]);
        onSuccess?.(additionalData);
      }
      return additionalData;
    }
    throw result.err;
  }

  const fetchAll = async () => {
    ignore = true;
    setStatus(STATUSES.FETCHING);
    resetData();
    resetError();
    try {
      let skip = 0;
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const result = await fetcher(skip);
        if (result === undefined || !needToFetchMore(result, limit)) {
          break;
        }
        onFetch?.(result);
        skip += limit;
      }
      setStatus(STATUSES.SUCCESS);
    } catch (e) {
      handleError(e as TError);
    }

    ignore = false;
  }

  useEffect(() => {
    if (enableAutoFetch && !ignore) {
      fetchAll().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    error,
    status,
    isIdle: status === STATUSES.IDLE,
    isFetching: status === STATUSES.FETCHING,
    isSuccess: status === STATUSES.SUCCESS,
    isFailure: status === STATUSES.FAILURE,
    resetData,
    resetError,
    fetchAll,
  };
}

export function useFetchAllQueryHelper<T>({onChange, data, status, initialStatus = STATUSES.IDLE}: {
  onChange: (newData: T, newStatus: Status) => void;
  data: T
  status: Status;
  initialStatus?: Status
}) {
  const {errorMsg} = useNotification();
  const [searchStatus, setSearchStatus] = useState<Status>(initialStatus);

  useEffect(() => {
    if (status !== STATUSES.IDLE) {
      // idle で dispatch すると、タブ切り替え等で以前のデータが消えてしまうので、idle は無視する
      onChange(data, status);
    }
    if (status === STATUSES.FAILURE) {
      errorMsg("データ取得中にエラーが発生しました。");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, status]);

  useEffect(() => {
    // 以前のステータスを保持することで、タブ切り替えなどで status が idle に戻ってもた結果 0 件だったのかを保持できるようにしている
    if (status === searchStatus || (status === STATUSES.IDLE && searchStatus === STATUSES.SUCCESS)) {
      return
    }
    setSearchStatus(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStatus, status]);

  return {searchStatus};
}

export function mergeStatuses(...statuses: Status[]) {
  if (statuses.includes(STATUSES.FAILURE)) {
    return STATUSES.FAILURE;
  }
  if (statuses.includes(STATUSES.FETCHING)) {
    return STATUSES.FETCHING;
  }
  if (statuses.every((s) => s === STATUSES.SUCCESS)) {
    return STATUSES.SUCCESS;
  }
  return STATUSES.IDLE;
}

export function FetchMessage<T extends Array<any>>(
  {
    data,
    status,
    idleMessage = <Message>検索キーワードを入力し、検索ボタンを押してください。</Message>,
    fetchingMessage = <Box sx={{width: "100%", textAlign: "center"}}><CircularProgress/></Box>,
    failureMessage = <Message>データ取得中にエラーが発生しました。</Message>,
    nodataMessage = <Message>該当データがありません。</Message>,
    isEmpty = (value: T) => value.length === 0,
  }: {
    data: T;
    status: Status;
    // eslint-disable-next-line react/require-default-props
    idleMessage?: React.ReactNode;
    // eslint-disable-next-line react/require-default-props
    fetchingMessage?: React.ReactNode;
    // eslint-disable-next-line react/require-default-props
    failureMessage?: React.ReactNode;
    // eslint-disable-next-line react/require-default-props
    nodataMessage?: React.ReactNode;
    // eslint-disable-next-line react/require-default-props
    isEmpty?: (data: T) => boolean;
  }
) {
  return (
    <>
      {isEmpty(data) && status === STATUSES.IDLE && idleMessage}
      {status === STATUSES.FETCHING && fetchingMessage}
      {status === STATUSES.FAILURE && failureMessage}
      {status === STATUSES.SUCCESS && isEmpty(data) && nodataMessage}
    </>
  );
}

export function Message({children}: { children: React.ReactNode }) {
  return (
    <Box sx={{width: "100%", textAlign: "center"}}>
      <Typography component='p' variant="subtitle1" sx={{fontWeight: "bold"}}>{children}</Typography>
    </Box>
  );
}
